import type { DAZN } from '@dazn/bootstrap-lib';

import { PublicAPIDeps } from './PublicAPIDeps';
import { setAccessor, Getter, Globals, Optionals } from './setAccessor';
import { setFuncProp, AnyFunction } from './setFuncProp';

export interface WithDeps {
    deps: PublicAPIDeps;
}
export type CreateNS = ReturnType<typeof createAPIBuilder>;
export type DAZNKey = keyof DAZN & string;
export type DAZNKeyOrNone = DAZNKey | null | undefined;
export type NestedPropName<NS> = NS extends DAZNKey
    ? keyof DAZN[NS] & string
    : DAZNKey;
export type DAZNPropValue<NS, PK> =
    // has ns
    NS extends DAZNKey
        ? PK extends keyof DAZN[NS]
            ? DAZN[NS][PK]
            : never
        : // no ns
        PK extends DAZNKey
        ? DAZN[PK]
        : never;

const namespacify = (ns: string | null | undefined, pn: string) =>
    ns ? [ns, pn].join('.') : pn;

export const createAPIBuilder =
    ({ window, trackUsage, logger, deps }: Globals & WithDeps) =>
    <Ns extends DAZNKeyOrNone>(ns?: Ns) => {
        const createProp = <S, Npn extends NestedPropName<Ns>>(
            pn: Npn,
            getter: Getter<DAZNPropValue<Ns, Npn>>,
            opts: Optionals<S> | null,
        ) =>
            setAccessor({
                window,
                trackUsage,
                logger,
                getter,
                propPath: namespacify(ns, pn),
                replacedWith: opts?.replacedWith,
                setter: opts?.setter,
                shouldNotManipulate: opts?.shouldNotManipulate,
            });

        const createFunc = <
            Npn extends NestedPropName<Ns>,
            Fn extends DAZNPropValue<Ns, Npn> & AnyFunction,
        >(
            pn: Npn,
            fn: Fn,
            decorator?: (fn: Fn, pn: string) => Fn,
        ) =>
            setFuncProp({
                window,
                trackUsage,
                propName: namespacify(ns, pn),
                fn,
                decorator,
            });

        return (
            createWithNS: (
                arg0: WithDeps & {
                    prop: typeof createProp;
                    func: typeof createFunc;
                    logger: Globals['logger'];
                },
            ) => void,
        ) => {
            void createWithNS({
                prop: createProp,
                func: createFunc,
                logger,
                deps,
            });
        };
    };
